import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import ButtonLink from "../Link";
import { IClassroom } from "../../src/interfaces";
import CopyURLToClipboardBtn from "../CopyURLToClipboardBtn";
import SocialIcons from "./SocialIcons";
import Difficulty from "../Difficulty";
import RoundSeparator from "../RoundSeparator";
import Time from "../Time";
import Button from "../Button";
import CallToAction from "./CallToAction";
import { useLoginContext } from "../../hooks";
import { TooltipPosition } from "../TooltipedComp";

interface HeroProps {
  classroom: IClassroom;
  handleLogin: () => void;
  isAcademyClassroom: boolean;
}

function Hero({ classroom, handleLogin, isAcademyClassroom }: HeroProps) {
  const { t } = useTranslation("classroom");
  const { t: tCommon } = useTranslation("common");
  const router = useRouter();
  const { isReadyToQuest, userCompletedQuestsAsynchronous } = useLoginContext();
  const [completedQuests, setCompletedQuests] = useState<number>(0);
  const [nextSlug, setNextSlug] = useState<string>("");

  useEffect(() => {
    if (isReadyToQuest) {
      let count = 0;
      classroom.quests.forEach((quest) => {
        if (userCompletedQuestsAsynchronous.includes(quest.collectionId)) {
          count += 1;
        }
      });

      setCompletedQuests(count);
    } else {
      setCompletedQuests(0);
    }
  }, [isReadyToQuest, userCompletedQuestsAsynchronous]);

  useEffect(() => {
    if (isReadyToQuest) {
      const questsWithCompletion = classroom.quests.map((quest) => ({
        ...quest,
        completed: userCompletedQuestsAsynchronous.includes(
          Number(quest.collectionId)
        ),
      }));
      const sortedQuest = questsWithCompletion.sort(
        (a, b) => Number(a.completed) - Number(b.completed)
      );
      if (sortedQuest.length > 0) {
        const nextQuest = sortedQuest[0];
        setNextSlug(nextQuest.slug);
      } else {
        setNextSlug("");
      }
    } else {
      setNextSlug("");
    }
  }, [isReadyToQuest, userCompletedQuestsAsynchronous]);

  const handleBack = () => {
    if (isAcademyClassroom) {
      router.push("/academy");
    } else {
      router.back();
    }
  };

  return (
    <div className="flex flex-row justify-center w-full overflow-hidden bg-black md:bg-[url('/images/background.png')] md:bg-cover">
      <div className="relative xl:px-[95px] pt-2 md:pb-[114px] flex lg:flex-row flex-col justify-between items-center z-0 lg:mt-[145px] mt-[105px] max-w-[1440px] w-full text-white">
        <div className="flex flex-col w-full lg:w-auto md:px-4">
          <div className="flex place-content-between">
            <div className="md:w-1/2 flex flex-col gap-6">
              {!classroom.isPrivate ? (
                <div className="flex items-center mb-4 px-4">
                  <ButtonLink
                    onClick={handleBack}
                    label={
                      isAcademyClassroom
                        ? t("goBackToClassrooms")
                        : tCommon("goBack")
                    }
                    inverted
                    svgIcon="arrow-left-white"
                    variant="white"
                    underline
                  />
                </div>
              ) : null}
              <img
                src={classroom.horizontalBannerUrl}
                alt="hero"
                className="w-full md:hidden"
              />
              <div className="display-5 md:text-display-3 uppercase px-4">
                {classroom.title}
              </div>
              <div className="flex md:gap-6 justify-between md:justify-normal bodyText text-white font-inter md:flex-row items-center px-4">
                {classroom.difficulty && (
                  <Difficulty difficulty={classroom.difficulty} />
                )}
                {classroom.difficulty && classroom.estimatedTime > 0 && (
                  <RoundSeparator />
                )}
                {classroom.estimatedTime > 0 && (
                  <Time estimatedTime={classroom.estimatedTime} />
                )}
              </div>
              <div className="bodyText px-4">{classroom.description}</div>
              {classroom.academy ? (
                <div className="px-4">
                  <Button
                    label={classroom.academy?.label}
                    href={classroom.academy?.url}
                    type="withWhiteBorder"
                    svgIcon="ExternalLink"
                    className="sm:hidden"
                    stretch
                  />
                  <Button
                    label={classroom.academy?.label}
                    href={classroom.academy?.url}
                    type="withWhiteBorder"
                    svgIcon="ExternalLink"
                    className="hidden sm:flex mb-10"
                  />
                </div>
              ) : null}
              <CallToAction
                completed={completedQuests}
                totalQuests={classroom.quests.length}
                nextQuestSlug={nextSlug}
                whatsNextLink="WhatsNext"
                handleLogin={handleLogin}
                className="px-4"
              />
              <div className="w-fit items-center flex flex-col md:hidden gap-3 mx-auto">
                <span className="tagText">{t("shareTheClassroom")}</span>
                <div className="flex gap-3">
                  <SocialIcons slug={classroom.slug} color="white" />
                  <CopyURLToClipboardBtn
                    color="white"
                    tooltipPosition={TooltipPosition.TOP_CENTER}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          className="absolute top-0 right-0 h-full w-2/5 items-end justify-center hidden md:flex"
          style={{
            background: `linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.48) 100%), url(${classroom.verticalBannerUrl}), no-repeat`,
            backgroundSize: "cover",
            backgroundPosition: "center center",
          }}
        >
          <div className="w-fit items-center hidden gap-3 mb-5 md:flex md:flex-col lg:flex-row">
            <span className="tagText">{t("shareTheClassroom")}</span>
            <div className="flex gap-3">
              <SocialIcons slug={classroom.slug} color="white" />
              <CopyURLToClipboardBtn
                color="white"
                tooltipPosition={TooltipPosition.TOP_CENTER}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Hero;
